import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useApolloClient, useReactiveVar, useMutation } from '@apollo/client';
import { CachePersistor } from 'apollo3-cache-persist';
import { Avatar, Menu, Dropdown, Space } from 'antd';
import { useHistory } from 'react-router-dom';

import { fromAppVar } from 'graphql/cache';
import Drawer from 'components/Drawer';
import SignOutIcon from 'components/Icons/SignOutIcon';
import Path from 'routes/path';
import utils from 'utils/utils';
import { getUserShortName } from 'helpers/users';
import { USER_LOGOUT } from 'graphql/mutations/userMutations';
import {
  isLoggedInVar,
  userProfileVar,
  userVar,
  proposalsVar,
  adminProfileVar,
  adminVar,
  teamMembersVar,
} from 'graphql/cache';
import { LOCKED_PAGES } from 'constants/index';

import './AccountMenu.scss';

// Without an explicit key antd assigns one like `item_6`, which the menu's
// onClick would then push onto history as if it were a route.
const SIGN_OUT_KEY = 'sign-out';

const AccountMenu = ({ user, isLocked, setCollapse, mobileVersion }) => {
  const { teamId } = user || {};
  const profile = useReactiveVar(userProfileVar);
  const client = useApolloClient();
  const [logoutMutation] = useMutation(USER_LOGOUT);
  const persistor = new CachePersistor({
    cache: client.cache,
    storage: window.localStorage,
  });

  const history = useHistory();
  const [showMenu, setShowMenu] = useState(false);

  const checkImpersonateOrAdmin = () => {
    if (user?.isAdmin || localStorage.getItem('proxy-user-id')) return true;
    return false;
  };

  const handleClick = async (e) => {
    try {
      // Revoke the token server-side so it can't be replayed after logout.
      // Best-effort: proceed with local logout even if this fails (e.g. offline).
      await logoutMutation();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to revoke token on logout', err);
    }
    isLoggedInVar(false);
    userProfileVar('');
    userVar('');
    proposalsVar('');
    teamMembersVar('');
    utils.deleteCookie('instanceId');
    await client.clearStore();
    await persistor.purge();
    localStorage.clear();
    history.push(Path.LOGIN);
  };

  const mismatchMenu = fromAppVar() !== 'wordpress' && (
    <Menu className="account-menu">
      <Menu.Item key={SIGN_OUT_KEY} onClick={handleClick}>
        <Space size="middle">
          Sign out <SignOutIcon />
        </Space>
      </Menu.Item>
    </Menu>
  );

  const menuProfile = (
    <Menu
      className="account-menu"
      onClick={(e) => {
        const path = e.key;
        if (path === SIGN_OUT_KEY) return;

        setCollapse(true);
        setShowMenu(false);
        if (path === 'admin') {
          if (!user?.isAdmin) {
            const adminData = adminVar();
            if (adminData !== null) {
              userVar(adminVar());
              userProfileVar(adminProfileVar());
              adminVar(null);
              adminProfileVar(null);
              localStorage.removeItem('proxy-token');
              localStorage.removeItem('proxy-user-id');
            }
          }
          return window.open(Path.ADMIN, '_self');
        }

        if (isLocked && LOCKED_PAGES.includes(path)) {
          return;
        }
        history.push(path);
      }}>
      {checkImpersonateOrAdmin() && <Menu.Item key="admin">Admin</Menu.Item>}

      <Menu.Item key={Path.PROFILE}>Profile</Menu.Item>
      {!teamId && <Menu.Item key={Path.PLANS}>Plans</Menu.Item>}
      {!teamId && (
        <Menu.Item key={Path.USERSTEAM} className={isLocked ? 'disabled' : ''}>
          Users
        </Menu.Item>
      )}
      <Menu.Item key={Path.ACTIVITY} className={isLocked ? 'disabled' : ''}>
        Activity
      </Menu.Item>
      {!teamId && <Menu.Item key={Path.AFFILIATE}>Affiliate Program</Menu.Item>}

      {fromAppVar() !== 'wordpress' && (
        <>
          <Menu.Divider />
          <Menu.Item key={SIGN_OUT_KEY} onClick={handleClick}>
            <Space size="middle">
              Sign out <SignOutIcon />
            </Space>
          </Menu.Item>
        </>
      )}
    </Menu>
  );

  if (mobileVersion) {
    return (
      <>
        <div className="user-avatar-mobile" onClick={() => setShowMenu(true)}>
          {profile?.image ? (
            <Avatar src={profile.image} size={40} />
          ) : (
            <Avatar size={40}>{getUserShortName({ name: profile?.name })}</Avatar>
          )}
        </div>
        <Drawer
          visible={showMenu}
          setVisible={() => setShowMenu(false)}
          direction="bottom"
          className="avatar-menu"
          content={
            window.location.pathname === Path.INVALID_CONNECTION ? mismatchMenu : menuProfile
          }
        />
      </>
    );
  }

  return (
    <Dropdown
      overlay={window.location.pathname === Path.INVALID_CONNECTION ? mismatchMenu : menuProfile}
      placement="topRight"
      overlayClassName="account-menu-wrapper"
      trigger="click">
      <div className="user-avatar">
        {profile?.image ? (
          <Avatar src={profile.image} size={40} />
        ) : (
          <Avatar size={40}>{getUserShortName({ name: profile?.name })}</Avatar>
        )}
      </div>
    </Dropdown>
  );
};

AccountMenu.defaultProps = {
  user: '',
  isLocked: false,
  setCollapse: () => {},
  mobileVersion: false,
};

AccountMenu.propTypes = {
  user: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
  isLocked: PropTypes.bool,
  setCollapse: PropTypes.func,
  mobileVersion: PropTypes.bool,
};

export default AccountMenu;
